// ABOUTME: Lets a data-entry user fill an image field from a link, either downloading it or keeping it.
// ABOUTME: Downloading returns a media id, keeping the link only validates it and stores the URL.

import { Box, Button, Stack, TextField, Typography } from '@ds/coolshop';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useState } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';

import { MediaUrlRejectedError } from '../api/mediaNodeApi';

const ASSET_URI = `${import.meta.env.VITE_API_BASE_URI}/media/`;

type AddMode = 'download' | 'link';

/**
 * The server answers a rejected URL with a stable `reason` code; unknown codes fall back to a
 * generic message so a new server-side reason never renders as a raw identifier.
 */
const reasonMessages = defineMessages({
    invalid_url: {
        id: 'add-image-from-url.invalid-url',
        defaultMessage: 'This is not a valid URL',
    },
    unsupported_protocol: {
        id: 'add-image-from-url.unsupported-protocol',
        defaultMessage: 'Only http and https links are supported',
    },
    credentials_not_allowed: {
        id: 'add-image-from-url.credentials-not-allowed',
        defaultMessage: 'Links containing a username or a password are not supported',
    },
    dns_lookup_failed: {
        id: 'add-image-from-url.dns-lookup-failed',
        defaultMessage: 'The host of this link could not be resolved',
    },
    private_address_not_allowed: {
        id: 'add-image-from-url.private-address-not-allowed',
        defaultMessage: 'This address cannot be reached',
    },
    fetch_failed: {
        id: 'add-image-from-url.fetch-failed',
        defaultMessage: 'The image could not be retrieved',
    },
    timeout: {
        id: 'add-image-from-url.timeout',
        defaultMessage: 'The host took too long to answer',
    },
    too_many_redirects: {
        id: 'add-image-from-url.too-many-redirects',
        defaultMessage: 'This link redirects too many times',
    },
    redirect_without_location: {
        id: 'add-image-from-url.redirect-without-location',
        defaultMessage: 'This link redirects to nowhere',
    },
    http_error: {
        id: 'add-image-from-url.http-error',
        defaultMessage: 'The host answered with an error',
    },
    not_an_image: {
        id: 'add-image-from-url.not-an-image',
        defaultMessage: 'This link does not point to an image',
    },
    image_too_large: {
        id: 'add-image-from-url.image-too-large',
        defaultMessage: 'This image is too large',
    },
});

const REASON_MESSAGES: Record<string, MessageDescriptor> = reasonMessages;

type AddImageFromUrlProps = {
    onAdd: (value: string) => void;
    disabled?: boolean;
    /**
     * Downloads the image instead of `POST /api/media/from-url`, answering with the value to add.
     * A MEDIA node's asset passes its replacement, which keeps the asset id: a download through the
     * default endpoint would store the image under a new one. Refusals throw
     * `MediaUrlRejectedError`, so they read exactly like the default endpoint's.
     */
    download?: (url: string) => Promise<string>;
};

const AddImageFromUrl = ({ onAdd, disabled, download }: AddImageFromUrlProps) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [url, setUrl] = useState('');
    const [mode, setMode] = useState<AddMode>('download');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const genericError = intl.formatMessage({
        id: 'add-image-from-url.generic-error',
        defaultMessage: 'This link could not be used',
    });

    const close = () => {
        setIsOpen(false);
        setUrl('');
        setError(null);
    };

    const confirm = async () => {
        const trimmedUrl = url.trim();
        if (!trimmedUrl) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            if (mode === 'download' && download) {
                onAdd(await download(trimmedUrl));
                close();
                return;
            }

            const response = await fetch(
                `${ASSET_URI}${mode === 'download' ? 'from-url' : 'validate-url'}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: trimmedUrl }),
                },
            );
            const data = await response.json();

            if (!response.ok) {
                const message = REASON_MESSAGES[data?.reason];
                setError(message ? intl.formatMessage(message) : genericError);
                return;
            }

            onAdd(mode === 'download' ? data[0] : trimmedUrl);
            close();
        } catch (caught) {
            const message =
                caught instanceof MediaUrlRejectedError
                    ? REASON_MESSAGES[caught.reason]
                    : undefined;
            setError(message ? intl.formatMessage(message) : genericError);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <Box paddingTop={1} textAlign="center">
                <Button
                    variant="text"
                    size="small"
                    disabled={disabled}
                    onClick={() => setIsOpen(true)}
                >
                    {intl.formatMessage({
                        id: 'add-image-from-url.open',
                        defaultMessage: 'Add from URL',
                    })}
                </Button>
            </Box>
        );
    }

    return (
        <Stack gap={1} paddingTop={1} data-testid="add-image-from-url">
            <TextField
                id="add-image-from-url-input"
                fullWidth
                size="small"
                type="url"
                autoFocus
                value={url}
                disabled={disabled || loading}
                label={intl.formatMessage({
                    id: 'add-image-from-url.input-label',
                    defaultMessage: 'Image URL',
                })}
                placeholder="https://"
                onChange={event => setUrl(event.target.value)}
                onKeyDown={event => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        confirm();
                    }
                }}
            />
            <ToggleButtonGroup
                exclusive
                color="primary"
                size="small"
                value={mode}
                disabled={disabled || loading}
                onChange={(_event, value) => {
                    if (value) {
                        setMode(value as AddMode);
                    }
                }}
            >
                <ToggleButton value="download" data-testid="add-image-from-url-download">
                    {intl.formatMessage({
                        id: 'add-image-from-url.mode-download',
                        defaultMessage: 'Download & store',
                    })}
                </ToggleButton>
                <ToggleButton value="link" data-testid="add-image-from-url-link">
                    {intl.formatMessage({
                        id: 'add-image-from-url.mode-link',
                        defaultMessage: 'Keep link only',
                    })}
                </ToggleButton>
            </ToggleButtonGroup>
            {error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}
            <Stack direction="row" gap={1}>
                <Button
                    size="small"
                    loading={loading}
                    disabled={disabled || !url.trim()}
                    onClick={confirm}
                    data-testid="add-image-from-url-confirm"
                >
                    {intl.formatMessage({
                        id: 'add-image-from-url.confirm',
                        defaultMessage: 'Confirm',
                    })}
                </Button>
                <Button variant="text" size="small" disabled={loading} onClick={close}>
                    {intl.formatMessage({
                        id: 'add-image-from-url.cancel',
                        defaultMessage: 'Cancel',
                    })}
                </Button>
            </Stack>
        </Stack>
    );
};

export default AddImageFromUrl;
